import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useState } from "react";
import { handlePagingAll } from "../api/APIUtilities";
import { postNotification } from "./Notifications";
import moment from "moment";

import { useConfirm } from "material-ui-confirm";
import StyledMenu from "../layout/StyledMenu";
import ModalLoadingFull from "../modals/ModalLoadingFull";

import * as Yup from "yup";
import { Formik } from "formik";
import { BCPEllipsis } from "../icons/bcp/BCPIcons";
import { deleteData, postData } from "./UtilityFunctions";

export default function Comments(props) {
  const { t } = useTranslation();
  const { cardId, armyListId, gameSystemId } = props;
  const authContext = useContext(AuthContext);
  const confirm = useConfirm();

  const [comments, setComments] = useState(null);
  const [comment, setComment] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const updateComments = (aComments, oComment, options) => {
    const del = options?.del;
    const push = options?.push;

    if (oComment.index) {
      var keys = oComment.index.split(".");
      let key = keys.shift();
      let aComment = aComments[key];
      if (!keys.length) {
        if (del) {
          aComments.splice(key, 1);
        } else {
          aComments[key] = oComment;
        }
      } else {
        while (keys?.length && aComment) {
          key = keys.shift();
          if (push || keys?.length) {
            aComment = aComment.comments?.[key];
          }
        }
        if (aComment) {
          if (push) {
            oComment.index = `${oComment.index}.${
              aComment.comments?.length || 0
            }`;
            if (aComment.comments) {
              aComment.comments.push(oComment);
            } else {
              aComment.comments = [oComment];
            }
          } else if (del) {
            aComment.comments.splice(key, 1);
          } else {
            aComment.comments[key] = oComment;
          }
        }
      }
    } else if (push) {
      oComment.index = `${aComments.length || 0}`;
      aComments.push(oComment);
    }

    setComments(aComments);
  };

  const showHideComment = async (oComment) => {
    setProcessing(true);
    try {
      try {
        var updatedComment = await postData(`/comments/${oComment.id}`, {
          body: {
            set: {
              public: !oComment.public,
            },
          },
        });
        updateComments([...comments], {
          ...oComment,
          ...updatedComment,
        });
        postNotification(
          "info",
          `Comment ${updatedComment.public ? "Unhidden" : "Hidden"}`
        );
      } catch (err) {
        postNotification("error", err);
      }
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  const deleteComment = async (oComment) => {
    try {
      await confirm({
        title: `Are you sure.`,
        description: "Are you sure you want to delete this comment?",
        dialogProps: {
          sx: {
            zIndex: 99999,
          },
        },
      });
    } catch (err) {
      return;
    }

    setProcessing(true);
    try {
      await deleteData(`/comments/${oComment.id}`);

      postNotification("info", t("notifications.info.commentDeleted"));

      updateComments([...comments], oComment, { del: true });
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  const replyComment = async (parentIndex, body) => {
    var sanitizedValues = {
      ...body,
      ownerId: authContext?.authState?.identity?.userId,
    };
    if (cardId) {
      sanitizedValues.cardId = cardId;
    }
    if (armyListId) {
      sanitizedValues.armyListId = armyListId;
    }

    setProcessing(true);
    try {
      var newComment = await postData("/comments", {
        body: sanitizedValues,
      });
      newComment.comments = [];
      updateComments(
        [...comments],
        {
          index: parentIndex,
          ...newComment,
        },
        { push: true }
      );
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  const fetchComments = async () => {
    var queryStringParameters = {
      "expand[]": ["comments", "owner"],
    };
    if (cardId) {
      queryStringParameters.cardId = cardId;
    }
    if (armyListId) {
      queryStringParameters.armyListId = armyListId;
    }

    try {
      var apiComments = await handlePagingAll(
        "/comments",
        queryStringParameters
      );
      setComments(apiComments);
    } catch (err) {
      postNotification("error", err);
      setComments(null);
    }
  };

  useEffect(() => {
    if (cardId || armyListId) {
      fetchComments();
    }
    // eslint-disable-next-line
  }, [cardId, armyListId]);

  return (
    <Grid
      container
      sx={{
        p: 2,
      }}
    >
      <Grid item xs={12}>
        <Typography variant="h3">Comments</Typography>
      </Grid>

      {!comments ? (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      ) : (
        <>
          {comments?.length > 0 &&
            comments.map((row, index) => {
              return (
                <Comment
                  key={index}
                  index={`${index}`}
                  comment={row}
                  authContext={authContext}
                  setOptionsAnchorEl={setOptionsAnchorEl}
                  setComment={setComment}
                  replyComment={replyComment}
                  gameSystemId={gameSystemId}
                />
              );
            })}

          {authContext?.authState?.identity?.userId && (
            <CommentReply
              authContext={authContext}
              index={comments.length}
              replyComment={replyComment}
              replyOpen={true}
            />
          )}
        </>
      )}

      <OptionsMenu
        anchorEl={optionsAnchorEl}
        setAnchorEl={setOptionsAnchorEl}
        comment={comment}
        setComment={setComment}
        showHideComment={showHideComment}
        deleteComment={deleteComment}
      />
      {processing && <ModalLoadingFull />}
    </Grid>
  );
}

const Comment = (props) => {
  const {
    comment,
    authContext,
    index,
    child,
    setOptionsAnchorEl,
    setComment,
    replyComment,
    gameSystemId,
  } = props;
  const createdAt = moment(comment.created_at);
  const [replyOpen, setReplyOpen] = useState(false);

  const isAdmin =
    authContext?.authState?.identity?.isAdmin === true ||
    (gameSystemId &&
      authContext?.authState?.identity?.adminGameSystems?.includes(
        gameSystemId
      ));

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{
          mt: 1,
          ml: child || null,
        }}
      >
        <Grid container>
          <Grid item sx={{ minWidth: "30px", maxWidth: "30px" }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: "0.9rem" }}>
              {comment.owner
                ? `${comment.owner.firstName?.[0] || ""}${
                    comment.owner.lastName?.[0] || ""
                  }`.trim()
                : "A"}
            </Avatar>
          </Grid>
          <Grid item sx={{ flexGrow: "1" }}>
            <Grid container>
              <Grid
                item
                xs={12}
                sx={{
                  p: 1,
                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: comment.public
                    ? "background.default"
                    : "error.light",
                }}
              >
                <Typography gutterBottom sx={{ fontWeight: "600" }}>
                  {comment.owner
                    ? `${comment.owner.firstName || ""} ${
                        comment.owner.lastName || ""
                      }`.trim()
                    : "Anonymous"}
                </Typography>
                <Typography gutterBottom>{comment.commentText}</Typography>
              </Grid>
              {authContext?.authState?.identity?.userId && (
                <Grid item xs={12}>
                  {isAdmin ? (
                    <IconButton
                      onClick={(e) => {
                        setComment({
                          ...comment,
                          index: index,
                        });
                        setOptionsAnchorEl(e.currentTarget);
                      }}
                    >
                      <BCPEllipsis />
                    </IconButton>
                  ) : (
                    <></>
                  )}
                  <Typography
                    variant="caption"
                    color="gray.main"
                    sx={{
                      ml: 1,
                      fontWeight: "inherit",
                      fontSize: "inherit",
                      lineHeight: "inherit",
                    }}
                  >
                    <Link
                      color="inherit"
                      sx={{
                        "&:hover": {
                          cursor: "pointer",
                        },
                      }}
                      onClick={() => {
                        setReplyOpen(!replyOpen);
                      }}
                    >
                      Reply
                    </Link>{" "}
                    {createdAt.fromNow()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {comment.comments?.length > 0 &&
        comment.comments.map((row, childIndex) => {
          return (
            <Comment
              key={childIndex}
              index={`${index}.${childIndex}`}
              child={child ? child + 1 : 1}
              comment={row}
              authContext={authContext}
              setOptionsAnchorEl={setOptionsAnchorEl}
              setComment={setComment}
              replyComment={replyComment}
              gameSystemId={gameSystemId}
            />
          );
        })}

      {replyOpen && (
        <CommentReply
          comment={comment}
          authContext={authContext}
          index={index}
          child={child}
          replyComment={replyComment}
          replyOpen={replyOpen}
          setReplyOpen={setReplyOpen}
        />
      )}
    </>
  );
};

const CommentReply = (props) => {
  const { comment, index, child, replyComment, setReplyOpen, authContext } =
    props;

  return (
    <Grid
      item
      xs={12}
      sx={{
        mt: 1,
        ml: !comment?.id ? null : child ? child + 1 : 1,
      }}
    >
      <Formik
        initialValues={{
          commentText: "",
        }}
        enableReinitialize
        validationSchema={Yup.object().shape({
          commentText: Yup.string().required("Required"),
        })}
        onSubmit={async (values, { resetForm }) => {
          try {
            var sanitizedValues = { ...values };
            if (comment?.id) {
              sanitizedValues.parentCommentId = comment.id;
            }
            await replyComment(index ? `${index}` : undefined, sanitizedValues);
            if (setReplyOpen) {
              setReplyOpen(false);
            }
            resetForm();
          } catch (err) {
            postNotification("error", err);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container>
              <Grid item sx={{ minWidth: "30px" }}>
                <Avatar sx={{ width: 24, height: 24, fontSize: "0.9rem" }}>
                  {`${
                    authContext?.authState?.identity?.firstName?.[0] ||
                    authContext?.authState?.identity?.given_name?.[0] ||
                    ""
                  }${
                    authContext?.authState?.identity?.lastName?.[0] ||
                    authContext?.authState?.identity?.family_name?.[0] ||
                    ""
                  }`.trim()}
                </Avatar>
              </Grid>
              <Grid item sx={{ flexGrow: "1", maxWidth: "calc(100% - 30px)" }}>
                <Grid
                  container
                  sx={{
                    p: 1,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.default",
                  }}
                >
                  <Grid item xs={12}>
                    <TextField
                      name="commentText"
                      label="Comment"
                      value={values.commentText}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      required
                      onBlur={handleBlur}
                      onChange={handleChange}
                      multiline
                      error={Boolean(touched.commentText && errors.commentText)}
                      helperText={touched.commentText && errors.commentText}
                      sx={{
                        "& textarea": { pr: "25px" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      color="success"
                      variant="contained"
                      type="submit"
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Grid>
  );
};

export function OptionsMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    comment,
    setComment,
    showHideComment,
    deleteComment,
  } = props;
  const open = Boolean(anchorEl);

  if (!comment) return <></>;

  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button",
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            await showHideComment(comment);
            setComment(null);
          }}
          disableRipple
        >
          {comment.public ? "Hide" : "Unhide"} Comment
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setAnchorEl(null);
            await deleteComment(comment);
            setComment(null);
          }}
          disableRipple
        >
          Delete Comment
        </MenuItem>
      </StyledMenu>
    </>
  );
}
